/**
 * Bundles of drying herbs hung from the apothecary's ceiling (lifted from
 * lantern-water's `drying-herbs.js`): dried sage, narrow rosemary and
 * flowering spikes as folded opaque triangles in the kit's shared botanical
 * material, each bundle tied with canvas cord. The sprigs draw from the
 * module's own seeded stream (1937), as the prototype's did. A once-built
 * piece: origin at the room's centre on the floor. No params.
 */
import * as THREE from 'three';
import type { Prefab } from './types.ts';

export const prefab: Prefab = {
  id: 'drying_herbs',
  buildPrefab(ctx) {
    const root = new THREE.Group();
    root.name = 'apothecary-drying-herbs';
    const { m } = ctx;
    const positions: number[] = [];
    const colors: number[] = [];
    let seed = 1937;
    const random = (): number => {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      return seed / 4294967296;
    };
    const tint = new THREE.Color();
    const tri = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, color: string, shade = 1): void => {
      tint.set(color).multiplyScalar(shade);
      for (const p of [a, b, c]) {
        positions.push(p.x, p.y, p.z);
        colors.push(tint.r, tint.g, tint.b);
      }
    };
    const stem = (a: THREE.Vector3, b: THREE.Vector3, radius: number): void => {
      const axis = b.clone().sub(a).normalize();
      const u = new THREE.Vector3(1, 0, 0).cross(axis).normalize();
      const v = axis.clone().cross(u);
      const offset = (j: number): THREE.Vector3 =>
        u
          .clone()
          .multiplyScalar(Math.cos((j * Math.PI * 2) / 3) * radius)
          .addScaledVector(v, Math.sin((j * Math.PI * 2) / 3) * radius);
      for (let i = 0; i < 3; i += 1) {
        const aa = a.clone().add(offset(i));
        const ab = a.clone().add(offset(i + 1));
        const ba = b.clone().add(offset(i));
        const bb = b.clone().add(offset(i + 1));
        tri(aa, ab, bb, '#988064');
        tri(aa, bb, ba, '#988064');
      }
    };
    const leaf = (base: THREE.Vector3, angle: number, length: number, width: number, color: string): void => {
      const direction = new THREE.Vector3(Math.cos(angle), -0.8, Math.sin(angle)).normalize();
      const side = new THREE.Vector3(-Math.sin(angle), 0, Math.cos(angle)).multiplyScalar(width);
      const center = base.clone().addScaledVector(direction, length * 0.48);
      const tip = base.clone().addScaledVector(direction, length);
      const ridge = center.clone().add(new THREE.Vector3(0, 0.018, 0));
      const left = center.clone().add(side);
      const right = center.clone().sub(side);
      tri(base, left, ridge, color, 0.83);
      tri(left, tip, ridge, color, 0.94);
      tri(base, ridge, right, color, 1.08);
      tri(right, ridge, tip, color, 1);
    };
    const tie = ctx.kit.shared('drying-herbs:tie', () => new THREE.TorusGeometry(0.042, 0.008, 4, 12));
    const greens = ['#899472', '#758766', '#9a936c'];
    [-5.5, -5.04, -0.72, -0.18, 3.7, 4.14, 4.62].forEach((x, index) => {
      const type = index % 3;
      const tieY = 4.06 - (index % 2) * 0.08;
      ctx.line(root, [x, 4.5, -4], [x, tieY, -4], 0.009, m.canvas);
      for (const y of [tieY, tieY - 0.027]) ctx.mesh(root, tie, m.canvas, x, y, -4).rotation.x = Math.PI / 2;
      ctx.line(root, [x + 0.03, tieY - 0.01, -3.96], [x + 0.09, tieY - 0.13, -3.96], 0.011, m.canvas);
      for (let sprig = 0; sprig < 7; sprig += 1) {
        const angle = sprig * 2.399 + index * 0.47;
        const top = new THREE.Vector3(x + Math.cos(angle) * 0.025, tieY + 0.09, -4 + Math.sin(angle) * 0.025);
        const ex = x + Math.cos(angle) * (0.11 + random() * 0.05);
        const ey = tieY - 0.48 - random() * 0.18;
        const end = new THREE.Vector3(ex, ey, -4 + Math.sin(angle) * 0.14);
        const mid = top.clone().lerp(end, 0.55);
        mid.x += Math.sin(angle) * 0.025;
        stem(top, mid, 0.006);
        stem(mid, end, 0.004);
        const count = type === 1 ? 6 : 4;
        for (let row = 0; row < count; row += 1) {
          const at = top.clone().lerp(end, 0.3 + row * (0.58 / count));
          for (const side of [-1, 1]) {
            const a = angle + side * 1.35 + random() * 0.25;
            const length = type === 1 ? 0.095 : 0.14 + random() * 0.055;
            leaf(at, a, length, type === 1 ? 0.014 : 0.04, greens[(sprig + row) % 3] ?? '#899472');
          }
        }
        if (type === 2) {
          // Small angular calyces cluster around the inverted flowering tip.
          for (let ring = 0; ring < 4; ring += 1) {
            const c = end.clone().lerp(mid, ring * 0.085);
            for (let bud = 0; bud < 4; bud += 1) {
              const a = angle + (bud * Math.PI) / 2;
              const tip = c.clone().add(new THREE.Vector3(Math.cos(a) * 0.043, -0.019, Math.sin(a) * 0.043));
              const left = c.clone().add(new THREE.Vector3(Math.cos(a + 0.9) * 0.023, 0.02, Math.sin(a + 0.9) * 0.023));
              const right = c.clone().add(new THREE.Vector3(Math.cos(a - 0.9) * 0.023, 0.02, Math.sin(a - 0.9) * 0.023));
              tri(c, left, tip, '#aa8d9f', 0.92);
              tri(c, tip, right, '#aa8d9f', 1.1);
            }
          }
        }
      }
    });
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.computeVertexNormals();
    ctx.mesh(root, geometry, ctx.kit.botanical, 0, 0, 0);
    return { root };
  },
};
